import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroSection from '../components/HeroSection';
import IntegrationsSection from '../components/IntegrationsSection';
import HowItWorksSection from '../components/HowItWorksSection';
import ExamplesSection from '../components/ExamplesSection';
import LiveDemoSection from '../components/LiveDemoSection';
import CtaSection from '../components/CtaSection';
import ValuePropositionSection from '../components/ValuePropositionSection';
import TrustSection from '../components/TrustSection';

export default function Home() {
  return (
    <>
      <Head>
        <title>Personalia - InDesign Automation & Personalization Platform</title>
        <meta name="description" content="Outgrown web-based design automation? Tired of HTML-to-PDF battles? Personalia turns your InDesign files into a high-speed API — pixel-perfect automation without rebuilding templates." />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <Header />
      
      <main id="main-content">
        <HeroSection />
        <ValuePropositionSection />
        
        {/* Competitive Positioning Section */}
        <section className="py-16 md:py-20 bg-white border-t border-gray-100">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Outgrown Your Current Tools?</h2>
              <p className="text-lg text-gray-600">
                If you've hit the ceiling with web-based design automation, you're not alone.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-md hover:border-primary hover:border-opacity-30 transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Outgrown Web-Canvas Tools?</h3>
                <p className="text-gray-600 mb-4">
                  Need more than "text on an image"? Complex brand guidelines that web-canvas tools can't replicate?
                </p>
                <p className="text-primary font-semibold">
                  Personalia renders your InDesign templates with full fidelity — not a browser screenshot.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-md hover:border-primary hover:border-opacity-30 transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Block-Based Editors Limiting You?</h3>
                <p className="text-gray-600 mb-4">
                  Your £500k+ proposals look like £15 templates? Block-based editors limiting your brand?
                </p>
                <p className="text-primary font-semibold">
                  Design in InDesign with full creative control, then automate.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-md hover:border-primary hover:border-opacity-30 transition-all">
                <h3 className="text-xl font-bold text-gray-900 mb-3">HTML-to-PDF Hell?</h3>
                <p className="text-gray-600 mb-4">
                  Tired of fighting CSS page breaks with Puppeteer or wkhtmltopdf?
                </p>
                <p className="text-primary font-semibold">
                  Let designers handle design. You power the production.
                </p>
              </div>
            </div>
            
            <div className="text-center mt-8">
              <Link href="/why-personalia" className="btn btn-primary">
                See the Full Comparison
              </Link>
            </div>
          </div>
        </section>
        
        <TrustSection />
        <ExamplesSection />

        <HowItWorksSection />

        <LiveDemoSection />
        <IntegrationsSection />
        
        {/* Use Cases Overview Section */}
        <section className="section" id="use-cases-overview">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Flexible for Any Workflow</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Designed to flex with your needs, Personalia supports diverse use cases across industries.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <Image 
                      src="/images/icons/InDesign.png" 
                      alt="InDesign Icon" 
                      width={64} 
                      height={64} 
                      className="h-16 w-16"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">Creative Authorities</h3>
                    <p className="text-gray-600 mb-4">
                      Create once, produce at scale. Automatically generate tailored outputs for every client and every request.
                    </p>
                    <a href="/for-creative-authorities" className="text-primary hover:underline font-medium">Learn more →</a>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <Image 
                      src="/images/icons/Developers.png" 
                      alt="Developers Icon" 
                      width={64} 
                      height={64} 
                      className="h-16 w-16"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">Infrastructure Engineers</h3>
                    <p className="text-gray-600 mb-4">
                    Integrate InDesign-quality design into your app, process, or workflow without needing design infrastructure.
                    </p>
                    <a href="/for-infrastructure-engineers" className="text-primary hover:underline font-medium">Learn more →</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <CtaSection />
      </main>

      <Footer />
    </>
  );
}
